/**
 * The Traffic page's first-party source (card 3d9c00e6): `/api/v1/analytics/traffic/first-party`
 * and its `/export` CSV. `analytics.read` to read, `exports.create` for the CSV.
 * Production unless environments are named, like every read.
 *
 * The website's own consented visits and orders, drilled down channel → source →
 * medium → campaign → creative → landing page under one attribution model. Every
 * figure is TmT-attributed (`source: 'tmt'`, `tmtAttributed`, the `model`) and is
 * never shown under the GA4 badge (blueprint 06 §6.10). A row names its node only —
 * no visitor, session, click id or referrer URL is in the payload.
 */

import { dataQualityFromWire, type ConversionCredit, type DataQuality, type DataQualityOut } from './dataQuality'
import type { Environment } from './types'

export const FIRST_PARTY_LEVELS = ['channel', 'source', 'medium', 'campaign', 'creative', 'landing_page'] as const
export type FirstPartyLevel = (typeof FIRST_PARTY_LEVELS)[number]

export const FIRST_PARTY_LEVEL_LABELS: Record<FirstPartyLevel, string> = {
  channel: 'Channel',
  source: 'Source',
  medium: 'Medium',
  campaign: 'Campaign',
  creative: 'Creative',
  landing_page: 'Landing page',
}

/** The level below each one; the landing page is the leaf. */
export const FIRST_PARTY_CHILD_LEVEL: Record<FirstPartyLevel, FirstPartyLevel | null> = {
  channel: 'source',
  source: 'medium',
  medium: 'campaign',
  campaign: 'creative',
  creative: 'landing_page',
  landing_page: null,
}

/** Blueprint 05 §5.8's three models, the default first, then paid click (the Meta and ads reads' default). */
export const ATTRIBUTION_MODELS = ['last_non_direct', 'last_touch', 'first_touch', 'paid_click'] as const
export type AttributionModel = (typeof ATTRIBUTION_MODELS)[number]

export const ATTRIBUTION_MODEL_LABELS: Record<AttributionModel, string> = {
  last_non_direct: 'Last non-direct',
  last_touch: 'Last touch',
  first_touch: 'First touch',
  paid_click: 'Paid click',
}

export const FIRST_PARTY_METRIC_KEYS = [
  'sessions',
  'users',
  'orders',
  'revenueMinor',
  'newCustomers',
  'conversionRate',
  'aovMinor',
] as const
export type FirstPartyMetricKey = (typeof FIRST_PARTY_METRIC_KEYS)[number]

export interface FirstPartyTrafficQuery {
  from: string
  to: string
  compare: boolean
  model: AttributionModel
  level: FirstPartyLevel
  /** The ancestors' keys, top first: exactly one per level above `level`. */
  path: readonly string[]
  environments: readonly Environment[]
  /** A metric's wire name or `label`, optionally `-` prefixed. The server's default is revenue, largest first. */
  sort?: string
  limit?: number
  offset?: number
}

export interface FirstPartyTrafficMetrics {
  sessions: number
  users: number
  orders: number
  revenueMinor: number
  newCustomers: number
  /** `orders / sessions`; null when there were no sessions. */
  conversionRate: number | null
  /** `revenue / orders`, minor units; null when there were no orders. */
  aovMinor: number | null
}

export interface FirstPartyTrafficRow extends FirstPartyTrafficMetrics {
  /** Null only for the unattributed row: orders with no touch in the lookback. */
  key: string | null
  /**
   * `unattributed` on that row — the **Not tracked** row, at the channel level only —
   * and `touch` on every other (Direct included). Absent from an API older than the
   * coverage card (wave 20 B1).
   */
  credit?: ConversionCredit
  label: string
  path: string[]
  model: AttributionModel
  tmtAttributed: true
  previous: FirstPartyTrafficMetrics | null
}

export interface FirstPartyTrafficReport {
  source: 'tmt'
  tmtAttributed: true
  model: AttributionModel
  level: FirstPartyLevel
  start: string
  end: string
  compared: boolean
  previousStart: string | null
  previousEnd: string | null
  environments: Environment[]
  nonProduction: boolean
  currency: string
  lookbackDays: number
  consentNote: string
  totalRows: number
  rows: FirstPartyTrafficRow[]
  totals: FirstPartyTrafficMetrics
  totalsPrevious: FirstPartyTrafficMetrics | null
  freshness: { newestTouchAt: string | null }
  /** The tracking baseline and the range's coverage, site-wide under the model (never the drill-down parent's); null from an older API. */
  dataQuality: DataQuality | null
}

export interface FirstPartyTrafficApi {
  firstPartyTraffic(query: FirstPartyTrafficQuery): Promise<FirstPartyTrafficReport>
  /** The same level as a CSV file (`exports.create`); a refusal rejects with the API's error. */
  firstPartyTrafficCsv(query: FirstPartyTrafficQuery): Promise<Blob>
}

// --- wire shapes (snake_case, as the API sends them) -------------------------

interface MetricsOut {
  sessions: number
  users: number
  orders: number
  revenue_minor: number
  new_customers: number
  conversion_rate: number | null
  aov_minor: number | null
}

type PreviousOut = {
  [K in keyof MetricsOut as `${K}_previous`]?: MetricsOut[K] | null
}

export interface FirstPartyTrafficRowOut extends MetricsOut, PreviousOut {
  key: string | null
  credit?: ConversionCredit
  label: string
  path: string[]
  model: AttributionModel
  tmt_attributed: true
}

export interface FirstPartyTrafficOut {
  source: 'tmt'
  tmt_attributed: true
  model: AttributionModel
  level: FirstPartyLevel
  parent: Record<string, string>
  start: string
  end: string
  compare: 'previous_period' | null
  previous_start: string | null
  previous_end: string | null
  environments: Environment[]
  non_production: boolean
  currency: string
  lookback_days: number
  consent_note: string
  sort: string
  limit: number
  offset: number
  total_rows: number
  rows: FirstPartyTrafficRowOut[]
  totals: MetricsOut
  totals_previous: MetricsOut | null
  freshness: { newest_touch_at: string | null; basis: 'newest_touch' }
  data_quality?: DataQualityOut | null
}

function metricsFromWire(body: MetricsOut): FirstPartyTrafficMetrics {
  return {
    sessions: body.sessions,
    users: body.users,
    orders: body.orders,
    revenueMinor: body.revenue_minor,
    newCustomers: body.new_customers,
    conversionRate: body.conversion_rate,
    aovMinor: body.aov_minor,
  }
}

/** A row's `*_previous` fields as metrics; null when the report was not compared. */
function previousFromWire(row: FirstPartyTrafficRowOut): FirstPartyTrafficMetrics | null {
  if (row.sessions_previous === null || row.sessions_previous === undefined) return null
  return {
    sessions: row.sessions_previous,
    users: row.users_previous ?? 0,
    orders: row.orders_previous ?? 0,
    revenueMinor: row.revenue_minor_previous ?? 0,
    newCustomers: row.new_customers_previous ?? 0,
    conversionRate: row.conversion_rate_previous ?? null,
    aovMinor: row.aov_minor_previous ?? null,
  }
}

export function firstPartyTrafficFromWire(body: FirstPartyTrafficOut): FirstPartyTrafficReport {
  return {
    source: body.source,
    tmtAttributed: body.tmt_attributed,
    model: body.model,
    level: body.level,
    start: body.start,
    end: body.end,
    compared: body.compare !== null,
    previousStart: body.previous_start,
    previousEnd: body.previous_end,
    environments: body.environments,
    nonProduction: body.non_production,
    currency: body.currency,
    lookbackDays: body.lookback_days,
    consentNote: body.consent_note,
    totalRows: body.total_rows,
    rows: body.rows.map((row) => ({
      ...metricsFromWire(row),
      key: row.key,
      ...(row.credit ? { credit: row.credit } : {}),
      label: row.label,
      path: row.path,
      model: row.model,
      tmtAttributed: row.tmt_attributed,
      previous: previousFromWire(row),
    })),
    totals: metricsFromWire(body.totals),
    totalsPrevious: body.totals_previous ? metricsFromWire(body.totals_previous) : null,
    freshness: { newestTouchAt: body.freshness.newest_touch_at },
    dataQuality: dataQualityFromWire(body.data_quality),
  }
}

/** The query string both the read and the CSV take, so the download is the level on screen. */
export function firstPartyTrafficSearch(query: FirstPartyTrafficQuery): string {
  const params = new URLSearchParams({ from: query.from, to: query.to, model: query.model, level: query.level })
  FIRST_PARTY_LEVELS.slice(0, query.path.length).forEach((level, index) => params.set(level, query.path[index] ?? ''))
  if (query.compare) params.set('compare', 'previous_period')
  if (query.sort) params.set('sort', query.sort)
  if (query.limit !== undefined) params.set('limit', String(query.limit))
  if (query.offset !== undefined) params.set('offset', String(query.offset))
  // Empty means the production default, decided by the server (as `withEnvironments`).
  for (const environment of query.environments) params.append('environment', environment)
  return params.toString()
}
